import { Link } from 'react-router-dom';
import { formatDateLabel } from '../daily';
import type { DailyResult } from '../dailySession';
import './dailyOverlay.css';

interface Props {
  outcome: 'won' | 'lost';
  result: DailyResult | null;
  dateKey: string;
  isToday: boolean;
  /** Overrides the stored subtitle — used the moment a round resolves. */
  subtitle?: string;
  /** Shown on a loss when the stored result carries no answer. */
  answer?: string;
}

/**
 * The end-of-round screen, shown both when a puzzle resolves and whenever the
 * player reopens a day they've already finished.
 *
 * A daily is one attempt: there is deliberately no "try again" here.
 */
export default function DailyResultOverlay({
  outcome,
  result,
  dateKey,
  isToday,
  subtitle,
  answer,
}: Props) {
  const won = outcome === 'won';
  const line = subtitle ?? result?.subtitle;
  const shownAnswer = answer ?? result?.answer;

  return (
    <div className="daily-overlay">
      <div className={'daily-overlay-card' + (won ? ' is-won' : ' is-lost')}>
        <div className="daily-overlay-emoji">{won ? '🎉' : '😬'}</div>
        <div className="daily-overlay-title">{won ? 'Solved!' : 'Out of luck'}</div>

        {line && <div className="daily-overlay-sub">{line}</div>}

        {!won && shownAnswer && (
          <>
            <div className="daily-overlay-sub">The answer was</div>
            <div className="daily-overlay-answer">{shownAnswer}</div>
          </>
        )}

        <div className="daily-overlay-date">{formatDateLabel(dateKey, isToday)}</div>
        <div className="daily-overlay-note">
          {isToday
            ? 'One puzzle a day — come back tomorrow for a fresh one.'
            : "Today's puzzle hasn't been published yet — this was the latest."}
        </div>
        <Link to="/" className="btn btn-primary">
          Back to games
        </Link>
      </div>
    </div>
  );
}
